/* assets/ctr-scroll-progress.js
   <ctr-scroll-progress> — custom scroll indicator for a native
   overflow-x scroll container. Expects, anywhere inside:
     [data-ctr-scroll-viewport]  the scrolling element
     [data-ctr-scroll-bar]       the track (optional)
     [data-ctr-scroll-thumb]     the thumb (optional)
   Auto-hides the bar when there is nothing to scroll.
*/

if (!customElements.get('ctr-scroll-progress')) {
  class CtrScrollProgress extends HTMLElement {
    static MIN_THUMB = 28; // px — keeps the thumb grabbable on long tracks

    connectedCallback() {
      this.viewport = this.querySelector('[data-ctr-scroll-viewport]');
      if (!this.viewport) return;

      this.bar = this.querySelector('[data-ctr-scroll-bar]');
      this.thumb = this.querySelector('[data-ctr-scroll-thumb]');

      this.frame = null;
      this.travel = 0;
      this.maxScroll = 0;
      this.dragging = false;

      this.requestUpdate = this.requestUpdate.bind(this);
      this.onBarPointerDown = this.onBarPointerDown.bind(this);
      this.onThumbPointerDown = this.onThumbPointerDown.bind(this);
      this.onPointerMove = this.onPointerMove.bind(this);
      this.onPointerUp = this.onPointerUp.bind(this);
      this.onBlockSelect = this.onBlockSelect.bind(this);

      this.viewport.addEventListener('scroll', this.requestUpdate, { passive: true });

      if (this.bar && this.thumb) {
        this.bar.addEventListener('pointerdown', this.onBarPointerDown);
        this.thumb.addEventListener('pointerdown', this.onThumbPointerDown);
      }

      // Card widths, lazy images and viewport resizes all change scrollWidth.
      if ('ResizeObserver' in window) {
        this.observer = new ResizeObserver(this.requestUpdate);
        this.observer.observe(this.viewport);
        Array.from(this.viewport.children).forEach((child) => this.observer.observe(child));
      } else {
        window.addEventListener('resize', this.requestUpdate);
      }

      // Theme editor: bring the selected card into view.
      if (window.Shopify && window.Shopify.designMode) {
        document.addEventListener('shopify:block:select', this.onBlockSelect);
      }

      this.update();
    }

    disconnectedCallback() {
      if (this.frame) cancelAnimationFrame(this.frame);
      if (this.viewport) this.viewport.removeEventListener('scroll', this.requestUpdate);
      if (this.bar) this.bar.removeEventListener('pointerdown', this.onBarPointerDown);
      if (this.thumb) this.thumb.removeEventListener('pointerdown', this.onThumbPointerDown);
      if (this.observer) this.observer.disconnect();
      window.removeEventListener('resize', this.requestUpdate);
      window.removeEventListener('pointermove', this.onPointerMove);
      window.removeEventListener('pointerup', this.onPointerUp);
      window.removeEventListener('pointercancel', this.onPointerUp);
      document.removeEventListener('shopify:block:select', this.onBlockSelect);
    }

    requestUpdate() {
      if (this.frame) return;
      this.frame = requestAnimationFrame(() => {
        this.frame = null;
        this.update();
      });
    }

    update() {
      if (!this.viewport || !this.bar || !this.thumb) return;

      const { scrollWidth, clientWidth, scrollLeft } = this.viewport;
      this.maxScroll = scrollWidth - clientWidth;

      // Nothing to scroll — hide the bar entirely.
      if (this.maxScroll <= 1 || clientWidth === 0) {
        this.bar.hidden = true;
        this.travel = 0;
        return;
      }
      this.bar.hidden = false;

      const barWidth = this.bar.clientWidth;
      const ratio = Math.min(1, clientWidth / scrollWidth);
      const thumbWidth = Math.max(
        Math.min(CtrScrollProgress.MIN_THUMB, barWidth),
        Math.round(barWidth * ratio)
      );

      this.travel = barWidth - thumbWidth;

      // scrollLeft is negative in RTL; abs() keeps the thumb in range.
      const progress = Math.min(1, Math.max(0, Math.abs(scrollLeft) / this.maxScroll));

      this.thumb.style.width = `${thumbWidth}px`;
      this.thumb.style.transform = `translate3d(${Math.round(progress * this.travel)}px, 0, 0)`;
    }

    /* ---- drag the thumb ---- */

    onThumbPointerDown(event) {
      if (event.button !== 0 && event.pointerType === 'mouse') return;
      if (this.travel <= 0) return;

      event.preventDefault();
      event.stopPropagation();

      this.dragging = true;
      this.pointerStartX = event.clientX;
      this.scrollStart = this.viewport.scrollLeft;

      // Snap + smooth scrolling both fight per-frame scrollLeft writes.
      this.viewport.classList.add('is-dragging');
      this.classList.add('is-dragging');

      window.addEventListener('pointermove', this.onPointerMove);
      window.addEventListener('pointerup', this.onPointerUp);
      window.addEventListener('pointercancel', this.onPointerUp);
    }

    onPointerMove(event) {
      if (!this.dragging || this.travel <= 0) return;
      const delta = event.clientX - this.pointerStartX;
      const next = this.scrollStart + (delta / this.travel) * this.maxScroll;
      this.viewport.scrollLeft = Math.min(this.maxScroll, Math.max(0, next));
    }

    onPointerUp() {
      if (!this.dragging) return;
      this.dragging = false;
      this.viewport.classList.remove('is-dragging');
      this.classList.remove('is-dragging');
      window.removeEventListener('pointermove', this.onPointerMove);
      window.removeEventListener('pointerup', this.onPointerUp);
      window.removeEventListener('pointercancel', this.onPointerUp);
      this.requestUpdate();
    }

    /* ---- click anywhere on the track to jump ---- */

    onBarPointerDown(event) {
      if (this.travel <= 0 || event.target === this.thumb) return;

      const rect = this.bar.getBoundingClientRect();
      const thumbWidth = this.thumb.offsetWidth;
      const position = event.clientX - rect.left - thumbWidth / 2;
      const progress = Math.min(1, Math.max(0, position / this.travel));

      this.viewport.scrollTo({
        left: progress * this.maxScroll,
        behavior: 'smooth'
      });
    }

    /* ---- theme editor ---- */

    onBlockSelect(event) {
      if (!this.contains(event.target)) return;
      const card = event.target.closest('[data-ctr-scroll-viewport] > *');
      if (!card) return;
      this.viewport.scrollTo({
        left: card.offsetLeft - this.viewport.offsetLeft,
        behavior: 'smooth'
      });
    }
  }

  customElements.define('ctr-scroll-progress', CtrScrollProgress);
}